import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, Space } from 'antd';
import dayjs from 'dayjs';
import { patchRequest, ResponseDataType } from 'api';
import { toast } from 'react-toastify';
import { DatePicker, parseFlexibleDate } from 'Components';

export interface ParentEditDataType {
  parentId: number;
  name: string;
  phoneNumber: string;
  email?: string;
  dob?: string;
}

interface EditParentModalProps {
  open: boolean;
  parentData: ParentEditDataType | null;
  onSuccess: () => void;
  onClose: () => void;
}

export const EditParentModal: React.FC<EditParentModalProps> = ({
  open,
  parentData,
  onSuccess,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (parentData) {
      form.setFieldsValue({
        name: parentData.name,
        phoneNumber: parentData.phoneNumber,
        email: parentData.email,
        dob: parseFlexibleDate(parentData.dob),
      });
    } else {
      form.resetFields();
    }
  }, [parentData, form]);

  const handleFinish = async (values: any) => {
    if (!parentData) return;
    setSubmitting(true);
    try {
      const dobString = values.dob
        ? dayjs(values.dob).isValid()
          ? dayjs(values.dob).format('YYYY-MM-DD')
          : undefined
        : undefined;

      const payload = {
        parentId: parentData.parentId,
        name: values.name?.trim(),
        phoneNumber: values.phoneNumber?.trim(),
        email: values.email?.trim(),
        dob: dobString,
      };

      const res: ResponseDataType<any> = await patchRequest('/cms/parent', payload);
      if (res?.code === 200) {
        toast.success('Cập nhật thông tin phụ huynh thành công!');
        onSuccess();
        onClose();
      } else {
        toast.error(res?.msg || 'Cập nhật phụ huynh thất bại!');
      }
    } catch (error) {
      toast.error('Có lỗi xảy ra khi cập nhật phụ huynh');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Chỉnh sửa thông tin phụ huynh"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={500}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        style={{ marginTop: 16 }}
      >
        <Form.Item
          label="Tên phụ huynh"
          name="name"
          rules={[{ required: true, message: 'Vui lòng nhập tên phụ huynh' }]}
        >
          <Input placeholder="Nhập tên phụ huynh" />
        </Form.Item>

        <Form.Item
          label="Số điện thoại"
          name="phoneNumber"
          rules={[{ required: true, message: 'Vui lòng nhập số điện thoại' }]}
          extra={
            <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
              💡 Lưu ý: Khi thay đổi số điện thoại, mật khẩu của phụ huynh sẽ tự động đặt lại theo số điện thoại mới.
            </div>
          }
        >
          <Input placeholder="Nhập số điện thoại" />
        </Form.Item>

        <Form.Item
          label="Email"
          name="email"
          rules={[
            { type: 'email', message: 'Email không hợp lệ' },
          ]}
        >
          <Input placeholder="Nhập email" />
        </Form.Item>

        <Form.Item label="Ngày sinh" name="dob">
          <DatePicker
            style={{ width: '100%' }}
            placeholder="Chọn hoặc dán ngày sinh"
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right', marginTop: 16 }}>
          <Space>
            <Button onClick={onClose} disabled={submitting}>
              Hủy
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Lưu thay đổi
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};
